import { useEffect, useRef, type FC, type ReactNode } from 'react';
import './Dialog.css';

interface DialogProps {
    open: boolean;
    /** Fechar sem decidir: Esc, clique fora, ou o botão neutro. */
    onClose: () => void;
    /**
     * A pergunta, **com o nome do que está em jogo dentro dela**. Quem lê só a
     * primeira linha já sabe o que está prestes a fazer: "Excluir o motivo
     * «Cliente sem interesse»?", não "Confirmar exclusão".
     */
    title: string;
    /** Uma linha sobre a consequência. O que se perde, o que continua. */
    children?: ReactNode;
    /**
     * O que o botão de confirmar faz — **um verbo, não "OK"**. "Excluir",
     * "Descartar", "Desativar". Com "OK"/"Cancelar" a pessoa tem que reler a
     * pergunta para saber qual botão é qual.
     */
    confirmLabel?: string;
    /** O caminho de volta. Também um verbo: "Continuar editando", "Cancelar". */
    cancelLabel?: string;
    onConfirm?: () => void;
    /**
     * Uma terceira saída, entre o neutro e o confirmar, para quando existe uma alternativa ao que
     * está sendo perguntado ("Tirar do funil em vez de excluir"). Opcional: quem não passa não muda.
     */
    extraLabel?: string;
    onExtra?: () => void;
    /** Pinta o confirmar de vermelho. Para o que não tem volta fácil. */
    danger?: boolean;
    /** Trava os botões enquanto a ação corre. */
    busy?: boolean;
    /**
     * Só avisar, sem perguntar — substitui o `alert()`. Esconde o cancelar e o
     * confirmar vira "Entendi".
     */
    aviso?: boolean;
}

/**
 * O diálogo do app: confirmar, descartar e explicar.
 *
 * Proposta de 2026-09-12 (mensagens-do-sistema.html), direções A+B. Substitui
 * o `confirm()` nativo (22 usos), o `alert()` (2) e o `ConfirmationModal`
 * pré-redesign (11 telas).
 *
 * Por que não dava para manter o `confirm()`: ele é desenhado pelo sistema
 * operacional — sem tema, sem a nossa tipografia, e nasce colado no topo da
 * janela, longe do botão que a pessoa acabou de clicar. Mesmo caso do
 * `<select>` nativo.
 *
 * **Exclusão em listagem NÃO usa este componente** — usa a confirmação na
 * própria linha (`ListKit`), que não interrompe. Este aqui é para o resto:
 * sair sem salvar, erro com conserto, e exclusão pedida de dentro de um
 * painel. Ver `padrao-de-tela.md`.
 */
const Dialog: FC<DialogProps> = ({
    open, onClose, title, children,
    confirmLabel = 'Confirmar', cancelLabel = 'Cancelar',
    onConfirm, extraLabel, onExtra, danger = false, busy = false, aviso = false,
}) => {
    const confirmarRef = useRef<HTMLButtonElement>(null);

    /**
     * Esc fecha **só o diálogo**, que é a camada de cima.
     *
     * `capture: true` + `stopPropagation` não é preciosismo: o `RecordPanel`
     * também escuta Esc no `document`, e o listener dele foi registrado antes
     * (o painel abriu antes). Sem a captura, o painel fechava e o diálogo
     * ficava — porque o `setState` do painel re-renderiza no meio da
     * propagação, o cleanup deste efeito tira o listener, e ele nunca chega a
     * ser chamado. Medido na tela: painel=0, diálogo=1.
     */
    useEffect(() => {
        if (!open) return;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || busy) return;
            e.stopPropagation();
            onClose();
        };
        document.addEventListener('keydown', aoTeclar, true);
        return () => document.removeEventListener('keydown', aoTeclar, true);
    }, [open, busy, onClose]);

    /* O foco vai para o botão de ação: quem abriu o diálogo pelo teclado
       continua no teclado, e Enter resolve. */
    useEffect(() => { if (open) confirmarRef.current?.focus(); }, [open]);

    if (!open) return null;

    return (
        <div className="ds-dialog-fundo" onClick={() => { if (!busy) onClose(); }}>
            <div
                className={`ds-dialog${!aviso && extraLabel && onExtra ? ' tem-extra' : ''}`}
                role="alertdialog"
                aria-modal="true"
                aria-label={title}
                onClick={e => e.stopPropagation()}
            >
                <h2 className="ds-dialog-t">{title}</h2>
                {children && <div className="ds-dialog-c">{children}</div>}

                <div className="ds-dialog-f">
                    {!aviso && (
                        <button type="button" className="ds-dialog-b" onClick={onClose} disabled={busy}>
                            {cancelLabel}
                        </button>
                    )}
                    {!aviso && extraLabel && onExtra && (
                        <button type="button" className="ds-dialog-b" onClick={onExtra} disabled={busy}>
                            {extraLabel}
                        </button>
                    )}
                    <button
                        type="button"
                        ref={confirmarRef}
                        className={`ds-dialog-b ${danger ? 'is-danger' : 'is-primary'}`}
                        onClick={aviso ? onClose : onConfirm}
                        disabled={busy}
                    >
                        {busy ? 'Aguarde…' : (aviso ? 'Entendi' : confirmLabel)}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Dialog;
